import { useCallback, useMemo, useState, type ReactNode } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { ConfirmContext, type ConfirmOptions } from './confirmContext';

interface PendingConfirm extends ConfirmOptions {
    resolve: (approved: boolean) => void;
}

/**
 * App-wide yes/no question behind `useConfirm`, for a flow that has to stop
 * and ask halfway through — the caller awaits the answer instead of every
 * screen that can start the flow mounting a dialog of its own.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [pending, setPending] = useState<PendingConfirm | null>(null);
    /**
     * Separate from `pending`, which outlives the answer: the dialog fades out
     * after closing, and with its options gone it would show an empty message
     * and the default "Löschen" for that moment.
     */
    const [open, setOpen] = useState(false);

    const confirm = useCallback(
        (options: ConfirmOptions) =>
            new Promise<boolean>((resolve) => {
                setPending({ ...options, resolve });
                setOpen(true);
            }),
        []
    );
    const answer = useCallback(
        (approved: boolean) => {
            pending?.resolve(approved);
            setOpen(false);
        },
        [pending]
    );
    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            <ConfirmDialog
                open={open}
                title={pending?.title}
                message={pending?.message}
                details={pending?.details}
                confirmLabel={pending?.confirmLabel}
                cancelLabel={pending?.cancelLabel}
                confirmColor={pending?.confirmColor}
                onConfirm={() => answer(true)}
                onCancel={() => answer(false)}
            />
        </ConfirmContext.Provider>
    );
}
